import {IMAGE_DEFAULT_EFFECTS} from '../../context/SelectedVariantContext/constants'

/**
 * Load an image and return its natural dimensions.
 * @param {string} src - Image URL
 * @returns {Promise<{naturalWidth: number, naturalHeight: number} | null>}
 */
function loadImageDimensions(src) {
    return new Promise((resolve) => {
        const img = new window.Image()
        img.crossOrigin = 'Anonymous'
        img.onload = () => {
            resolve({naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight})
        }
        img.onerror = () => {
            resolve(null)
        }
        img.src = src
    })
}

/**
 * Convert editor-core serialized nodes to Step 2 layers.
 * Maps each node directly using its geometry — no content-bounds scaling.
 * Positions are placed relative to the Step 2 background center.
 *
 * For image nodes, the function loads the image to determine natural
 * dimensions when they are not stored in node.content. This ensures
 * ImageLayer (which uses fillPatternImage at 1:1 pixel size) displays
 * the full image instead of cropping to the geometry rect.
 *
 * @param {{ nodes: Array, order?: string[] }} document - Serialized editor-core document
 * @param {{ x: number, y: number, width: number, height: number, dpi?: number, ratioDefault?: number }} background - Step 2 background/safe-zone
 * @returns {Promise<Array>} Step 2 layer objects
 */
export async function mapNodesToLayers(
    document,
    background,
    bounds = null,
    safeZoneScale = {scaleX: 1, scaleY: 1}
) {
    if (!document?.nodes?.length || !background) return []

    const {x: bgX, y: bgY, width: bgWidth, height: bgHeight, dpi: bgDpi} = background

    // Background center in Step 2 coordinate space
    const bgCenterX = bgWidth / 2 + bgX
    const bgCenterY = bgHeight / 2 + bgY

    // Determine the center of the shape elements from Step 1 based on the bounds payload
    const boundsCenterX = bounds ? bounds.x + bounds.width / 2 : bgCenterX;
    const boundsCenterY = bounds ? bounds.y + bounds.height / 2 : bgCenterY;

    // Use order to maintain layer ordering (first = bottom, last = top)
    // Reverse to match Step 2 rendering: Step 2 renders layers in array order (0 = bottom)
    const order = document.order ?? document.nodes.map((n) => n.id)
    const nodeMap = new Map(document.nodes.map((n) => [n.id, n]))
    const orderedNodes = [...order].reverse().map((id) => nodeMap.get(id)).filter(Boolean)

    const layers = []

    for (const node of orderedNodes) {
        const g = node.geometry || {}
        const nodeX = g.x ?? 0
        const nodeY = g.y ?? 0
        const nodeW = g.width ?? 100
        const nodeH = g.height ?? 50
        const nodeRotation = g.rotation ?? 0

        const id = Date.now().toString() + '_' + Math.random().toString(36).substring(2, 10)

        const nodeCenterX = nodeX + nodeW / 2;
        const nodeCenterY = nodeY + nodeH / 2;

        const mappedCenterX = bgCenterX + (nodeCenterX - boundsCenterX) * safeZoneScale.scaleX;
        const mappedCenterY = bgCenterY + (nodeCenterY - boundsCenterY) * safeZoneScale.scaleY;

        if (node.type === 'text') {
            const text = node.content?.text ?? 'Text'
            const fontSize = node.style?.fontSize ?? 60

            layers.push({
                id,
                text,
                fill: node.style?.fill || node.style?.color || '#000000',
                textColor: node.style?.fill || node.style?.color || '#000000',
                align: node.style?.align || 'left',
                fontWeight: node.style?.fontWeight || '400',
                fontFamily: node.style?.fontFamily || 'Roboto',
                fontStyle: node.style?.fontStyle || '',
                layerType: 'text',
                x: mappedCenterX,
                y: mappedCenterY,
                width: nodeW * safeZoneScale.scaleX,
                height: nodeH * safeZoneScale.scaleY,
                offsetX: (nodeW * safeZoneScale.scaleX) / 2,
                offsetY: (nodeH * safeZoneScale.scaleY) / 2,
                fontSize: fontSize * safeZoneScale.scaleX, // scale font size proportionately
                rotation: nodeRotation,
                scaleX: 1,
                scaleY: 1,
                draggable: true,
                isLock: false,
            })
        } else if (node.type === 'image') {
            const src = node.content?.src ?? ''
            if (!src) continue

            // Use natural image dimensions from node content if available.
            // If not stored (e.g. library images), load the image to determine them.
            // ImageLayer renders via fillPatternImage at natural pixel size,
            // so width/height must be natural dims with scale to fit geometry.
            let natW = node.content?.naturalWidth
            let natH = node.content?.naturalHeight

            if (!(natW > 0 && natH > 0)) {
                const dims = await loadImageDimensions(src)
                if (dims) {
                    natW = dims.naturalWidth
                    natH = dims.naturalHeight
                }
            }

            const useNatural = natW > 0 && natH > 0

            const layerWidth = useNatural ? natW : nodeW
            const layerHeight = useNatural ? natH : nodeH
            const baseScaleX = useNatural ? nodeW / natW : 1
            const baseScaleY = useNatural ? nodeH / natH : 1

            const scaleX = baseScaleX * safeZoneScale.scaleX
            const scaleY = baseScaleY * safeZoneScale.scaleY

            layers.push({
                id,
                src,
                tinySrc: src,
                linkS3: src,
                text: '',
                width: layerWidth,
                height: layerHeight,
                draggable: true,
                layerType: 'image',
                isLock: false,
                rotation: nodeRotation,
                scaleX,
                scaleY,
                offsetX: layerWidth / 2,
                offsetY: layerHeight / 2,
                x: mappedCenterX,
                y: mappedCenterY,
                bgDpi: bgDpi,
                imageType: 'image/png',
                ...IMAGE_DEFAULT_EFFECTS,
            })
        }
    }

    return layers
}
